/**
 * Session boot + auth-driven workspace mounting. Runs once from App.
 */
import { useEffect } from "react";
import { useAuth } from "./auth";
import { switchWorkspace, syncSharedMounts } from "./store";
import { migrateLocalToCloud } from "./migrate";
import { LOCAL_WORKSPACE_ID } from "./workspace";
import { useSharedFolders, sharedFoldersApi } from "./sharedFolders";
import { initPwa, handleLaunchAction } from "@/pwa";
import { loadAgentConfig } from "./agentConfig";
import { applySettingsToCss } from "@/store/settings";
import { isStandalone } from "@/lib/standalone";
import { seedOnboarding } from "./onboarding";
import { ensureDefaultTaskList } from "./taskBoot";
import { toast } from "@/ui/toast";

declare global {
  interface Window {
    /** Loads hep.gg analytics once; defined inline in index.html. */
    hnAnalytics?: () => void;
  }
}

const INVITE_STASH_KEY = "hn.pendingInvite";

/** Redeem a stashed invite token once signed in, then refresh folders. */
async function redeemPendingInvite(): Promise<void> {
  const token = localStorage.getItem(INVITE_STASH_KEY);
  if (!token) return;
  localStorage.removeItem(INVITE_STASH_KEY);
  try {
    await sharedFoldersApi.accept(token);
    toast.success("Invite accepted. The shared folder is in your sidebar.");
  } catch (err) {
    const message = err instanceof Error ? err.message : "";
    if (message === "mismatch") {
      toast.error("That invite was sent to a different email address.");
    } else {
      toast.error("That invite expired or was revoked. Ask for a new one.");
    }
  }
}

/** ?invite= carries either a raw invite token (signed out) or a status. */
function consumeInviteParam(value: string): void {
  if (/^[0-9a-z]{32}$/.test(value)) {
    // a token: stash it; it is redeemed right after sign-in
    localStorage.setItem(INVITE_STASH_KEY, value);
    if (useAuth.getState().status !== "signedIn") {
      toast.info("Sign in to accept the shared folder invite.");
    }
    return;
  }
  if (value === "expired") toast.error("That invite expired or was revoked. Ask for a new one.");
  if (value === "mismatch") toast.error("That invite was sent to a different email address. Sign in with that address to accept it.");
  if (value === "full") toast.error("That folder is full: it already has the maximum number of members.");
}

export function useBootstrap(): void {
  useEffect(() => {
    initPwa();
    loadAgentConfig();
    handleLaunchAction();
    const unsubSettings = applySettingsToCss();
    // anonymous local seeding only exists in the installed app. Sequenced:
    // if the default Tasks list landed first, seedOnboarding's fresh-workspace
    // guard would see a non-empty tree and skip the welcome note entirely.
    if (isStandalone()) {
      void seedOnboarding().then(() => ensureDefaultTaskList());
    }
    // surface auth redirect results (?auth=ok|expired|failed) and invites
    const params = new URLSearchParams(location.search);
    const authResult = params.get("auth");
    const inviteParam = params.get("invite");
    const openFolder = params.get("sf");
    const discordResult = params.get("discord");
    if (authResult || inviteParam || openFolder || discordResult) {
      params.delete("auth");
      params.delete("invite");
      params.delete("sf");
      params.delete("discord");
      const qs = params.toString();
      history.replaceState(null, "", location.pathname + (qs ? `?${qs}` : ""));
      if (authResult === "ok") toast.success("Signed in");
      if (authResult === "expired") toast.error("That sign-in link expired or was already used. Request a new one.");
      if (authResult === "device") toast.error("That sign-in link was requested in the Hydra Notes app. Open it on the iPhone or iPad that asked for it.");
      if (authResult === "failed") toast.error("Sign-in failed. Try again.");
      if (inviteParam) consumeInviteParam(inviteParam);
      if (discordResult) {
        // resume the link wizard where the OAuth redirect interrupted it
        void import("@/discord/useDiscordStore").then(async ({ consumeWizardState }) => {
          const pending = consumeWizardState();
          const { useDiscordLinkDialog } = await import("@/discord/DiscordLinkDialog");
          if (discordResult === "ok") {
            toast.success("Discord connected");
            useDiscordLinkDialog
              .getState()
              .openWizard(pending?.listId ? { listId: pending.listId, spaceId: pending.spaceId } : undefined);
          } else if (discordResult === "taken") {
            toast.error("That Discord account is already connected to a different Hydra Notes account.");
          } else {
            toast.error("Discord connection failed. Try again.");
          }
        });
      }
      if (authResult === "signin" && isStandalone()) {
        // in a browser tab the AuthGate IS the sign-in UI; only the
        // installed app needs the dialog opened explicitly
        setTimeout(() => {
          if (useAuth.getState().status !== "signedIn") {
            void import("@/components/SignInDialog").then((m) => m.useSignInDialog.getState().setOpen(true));
          }
        }, 400);
      }
    }
    // launch params holding secrets are gone now (above, and in
    // handleLaunchAction), so a deferred analytics load is safe; see index.html
    window.hnAnalytics?.();

    void useAuth.getState().refresh();

    // re-check the session (and folder list) when returning to the app
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void useAuth.getState().refresh();
        if (useAuth.getState().status === "signedIn") void useSharedFolders.getState().refresh();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    // mount the right workspace whenever auth state changes
    const unsub = useAuth.subscribe((state, prev) => {
      if (state.status === "signedIn" && state.workspaceId && state.workspaceId !== prev.workspaceId) {
        const ctx = switchWorkspace(state.workspaceId);
        void migrateLocalToCloud(ctx).then(async () => {
          // brand-new cloud accounts get the welcome note too; sequenced so
          // the default-list seed cannot trip the fresh-workspace guard
          await seedOnboarding();
          // existing accounts too, not just empty ones: the default Tasks
          // list is guaranteed for everyone (waits for first tree sync)
          void ensureDefaultTaskList();
        });
        void redeemPendingInvite().then(() => useSharedFolders.getState().refresh());
      } else if (state.status === "signedOut" && prev.status === "signedIn") {
        switchWorkspace(LOCAL_WORKSPACE_ID);
        useSharedFolders.setState({ status: "idle", folders: [] });
      }
    });

    // mount/unmount shared folder contexts as the membership list changes
    const unsubFolders = useSharedFolders.subscribe((state) => {
      if (state.status === "ready") syncSharedMounts(state.folders);
    });

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      unsub();
      unsubFolders();
      unsubSettings();
    };
  }, []);
}
